/**
 * Composes `ErrorSurface` (rizzui's `Alert`), as `../app-error.tsx` does — the vendor template
 * ships no "this address means several records" screen to name here.
 *
 * Billing answers 409 when two invoice series render the same number. The number URL cannot say
 * which record it means, so this lists the records it could (each by its id URL, which stays put
 * for an ambiguous number). Credit notes have no admin list to find them in, so for those it says
 * what Billing said and points at the invoice list, where the invoice the note was raised against is.
 */
import { Link, useRouteError } from "react-router-dom";
import { ErrorSurface } from "@steelyard/theme";
import AppError from "../app-error";
import { AmbiguousNumberError } from "../../lib/services/invoicing";

export function AmbiguousNumber({ error }: { error: AmbiguousNumberError }) {
  return (
    <ErrorSurface error={error.problem} action="open this number">
      {error.matches.length > 0 ? (
        <ul>
          {error.matches.map((match) => (
            <li key={match.href}>
              <Link to={match.href} className="font-medium underline">
                {match.label}
              </Link>{" "}
              · {match.detail}
            </li>
          ))}
        </ul>
      ) : (
        <Link to="/invoicing" className="font-medium underline">
          Find it from the invoice list
        </Link>
      )}
    </ErrorSurface>
  );
}

/** `errorElement` for every route keyed by an invoice or credit-note number; anything else is `AppError`'s. */
export default function NumberRouteError() {
  const error = useRouteError();
  if (!(error instanceof AmbiguousNumberError)) return <AppError />;
  return (
    <div className="mx-auto max-w-2xl p-6">
      <AmbiguousNumber error={error} />
    </div>
  );
}
